<template>
  <div>
    <div v-if="isLoading">
      <div class="flex justify-center items-center h-screen">
        <img :src="loader" alt="Loading..." class="w-8 h-8" />
      </div>
    </div>
    <div v-else class="px-6 py-4 text-left modal-content">
      <!--Body-->
      <div>
        <!-- tg preview -->
        <h3 class="font-bold text-3xl mb-2 capitalize">{{ tg.title }}</h3>
        <h4 class="text-xl mb-4 capitalize font-semibold">{{ tg.subtitle }}</h4>
        <div class="flex flex-wrap gap-2 mb-4">
          <span v-for="tag in tg.tags" :key="tag" class="px-2 py-1 bg-gray-200 rounded-md capitalize">
            {{ tag.tag_name }}
          </span>
        </div>
        <div v-if="tg.type_id === 1">
          <img :src="tg.thumbnail_image" alt="Thumbnail Preview" class="w-full h-96 object-cover border rounded-md mb-4 p-1" />
        </div>
        <div v-else-if="tg.type_id === 2">
          <video controls class="w-full h-auto object-cover border rounded-md mb-4 p-1" :poster="tg.thumbnail_image">
            <source :src="tg.banner_video" type="video/mp4" />
          </video>
        </div>
        <div v-else-if="tg.type_id === 3" class="overflow-x-auto whitespace-nowrap flex gap-2 mb-2">
          <div v-for="(image, index) in JSON.parse(tg.carousel)" :key="index" class="shrink-0">
            <video v-if="isVideo(index)" controls class="w-full h-96 object-cover rounded-md mb-2 border">
              <source :src="image" type="video/mp4" />
            </video>
            <img v-else :src="image" :alt="'Carousel Image ' + (index + 1)" class="w-full h-96 object-cover border rounded-md mb-4 p-1" />
          </div>
        </div>
        <div>
          <div class="content" v-html="tg.body"></div>
        </div>
        <!-- comments section -->
        <div v-if="tg.status?.name === 'pending' || tg.status?.name === 'approved' || tg.status?.name === 'published' || tg.status?.name === 'refused'" class="mt-8">
          <hr class="my-4" />
          <div>
            <p class="text-xl font-semibold">Feedbacks and Notes</p>
            <div v-for="comment in comments" :key="comment.id" class="flex items-center gap-2 mt-4 mb-2">
              <div class="flex flex-col border rounded-md p-4 bg-gray-100 w-full">
                <a v-if="comment?.media" :href="comment.media" target="_blank" class="text-sm text-blue-500">📎 view attachment</a>
                <p class="text-md font-semibold capitalize flex gap-3 items-center">
                  {{ comment.content }}
                  <svg
                    v-if="!isCommentUpdate"
                    @click="isCommentUpdate = true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-4 h-4 cursor-pointer"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                    />
                  </svg>
                </p>
                <!-- if comment updates -->
                <div v-if="isCommentUpdate" class="flex items-center gap-2 mt-2">
                  <input type="text" v-model="updatedComment" class="w-full px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button @click="updateComment(comment.id)" class="px-2 py-1 text-white bg-blue-500 rounded-lg hover:bg-blue-700 duration-300 text-sm">Update</button>
                  <button @click="isCommentUpdate = false" class="px-2 py-1 text-white bg-red-500 rounded-lg hover:bg-red-700 duration-300 text-sm">Cancel</button>
                </div>
                <p class="text-xs text-gray-500">
                  {{ comment.created_by.name }} | {{ formatDateTime(comment.created_at) }} <span class="italic">(Updated at: {{ formatDateTime(comment.updated_at) }})</span>
                </p>
              </div>
            </div>
            <hr class="my-2" />
            <!-- comment media preview -->
            <div v-if="commentMediaPreview" class="flex items-center gap-2 mt-4 mb-2">
              <img :src="commentMediaPreview" alt="Comment Media Preview" class="w-24 h-20 object-cover border rounded-sm mb-1 p-1" />
              <button @click="commentMediaPreview = ''" class="text-red-500 hover:text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="flex gap-2 mt-2">
              <input
                v-model="newComment"
                type="text"
                placeholder="Add a comment..."
                class="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                @keyup.enter="storeComment()"
              />
              <!-- button to upload images -->
              <label class="cursor-pointer px-6 py-3 mx-2 text-gray-400 rounded-lg bg-gray-200 hover:bg-gray-300 hover:text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                  />
                </svg>
                <input type="file" accept="image/*" class="hidden" @change="handleCommentMediaUpload" />
              </label>
              <button @click="storeComment()" class="p-3 px-6 py-3 mr-2 text-white bg-blue-500 rounded-lg hover:bg-blue-700 hover:text-white focus:outline-none duration-300">
                Send
              </button>
            </div>
          </div>
        </div>
        <!-- tg information -->
        <hr class="my-8" />
        <div class="flex items-center justify-center gap-1 mb-4 px-2 py-1 bg-gray-200 rounded-md capitalize w-20 font-semibold">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5 text-gray-700">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
            />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
          {{ tg.views }}
        </div>
        <div>
          <div class="grid grid-cols-3 gap-4">
            <div>
              <p class="text-lg font-semibold">SEO</p>
              <p class="text-sm text-gray-500 mb-2">SEO title: {{ tg.seo_title }}</p>
              <p class="text-sm text-gray-500 mb-2">SEO description: {{ tg.seo_description }}</p>
              <p class="text-sm text-gray-500 mb-2">Canonical tag: {{ tg.canonical_tag }}</p>
            </div>
            <div>
              <p class="text-lg font-semibold">Blog</p>
              <p class="text-sm text-gray-500 mb-2">Category: {{ tg.category?.name }}</p>
              <p class="text-sm text-gray-500 mb-2">Type: {{ tg.type?.name }}</p>
              <p class="text-sm text-gray-500 mb-2">
                Status:
                <span
                  :class="
                    tg.status?.name === 'published'
                      ? 'text-blue-500 bg-blue-100'
                      : tg.status?.name === 'approved'
                      ? 'text-green-500 bg-green-100'
                      : tg.status?.name === 'refused'
                      ? 'text-red-500 bg-red-100'
                      : 'text-amber-500 bg-amber-100'
                  "
                  class="inline-flex px-2 py-1 text-xs font-semibold leading-5 rounded-full"
                  >{{ tg.status?.name }}</span
                >
              </p>
            </div>
            <div>
              <p class="text-lg font-semibold">Other</p>
              <p class="text-sm text-gray-500 mb-2">Created by: {{ tg.created_by?.name }}</p>
              <p class="text-sm text-gray-500 mb-2">Created at: {{ formatDate(tg.created_at) }}</p>
              <p class="text-sm text-gray-500 mb-2">Approved by: {{ tg.approved_by || "N/A" }}</p>
              <p class="text-sm text-gray-500 mb-2">Published at: {{ tg.published_at || "N/A" }}</p>
            </div>
            <div>
              <p class="text-lg font-semibold">Notes</p>
              <p class="text-sm text-gray-500 mb-2 text-justify">{{ tg.notes }}</p>
            </div>
          </div>
        </div>
      </div>
      <!--Footer-->
      <div v-if="userRoleId === '2' && tg.status?.name === 'pending'" class="flex justify-end pt-2">
        <button @click="updateTgStatus('refused')" class="p-3 px-6 py-3 mr-2 text-red-500 bg-transparent rounded-lg hover:bg-red-100 hover:text-red-400 focus:outline-none">
          Refuse
        </button>
        <button
          @click="updateTgStatus('approved')"
          type="submit"
          class="px-6 py-3 font-medium tracking-wide text-white bg-indigo-600 rounded-md hover:bg-indigo-500 focus:outline-none"
        >
          Approve
        </button>
      </div>
      <div v-else-if="userRoleId === '2' && (tg.status?.name === 'approved' || tg.status?.name === 'unpublished')">
        <hr class="my-4" />
        <div class="flex justify-end items-center gap-3 pt-2">
          <span v-if="tg.status?.name === 'unpublished'" class="text-sm text-gray-500">
            This post was previously published but is currently unpublished. Click here to republish and make it visible to readers again.
          </span>
          <button
            @click="updateTgStatus('published')"
            type="submit"
            class="px-6 py-3 font-medium tracking-wide text-white bg-green-600 rounded-md hover:bg-green-500 focus:outline-none"
          >
            Publish
          </button>
        </div>
      </div>
      <div v-else-if="userRoleId === '2' && tg.status?.name === 'published'">
        <hr class="my-4" />
        <div class="flex justify-end items-center gap-3 pt-2">
          <button
            @click="updateTgStatus('unpublished')"
            type="submit"
            class="px-6 py-3 font-medium tracking-wide text-white bg-orange-500 rounded-md hover:bg-orange-600 focus:outline-none"
          >
            Unpublish
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ref, watch, onMounted, computed } from "vue";
import axios from "axios";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import Swal from "sweetalert2";
const baseUrl = process.env.VUE_APP_BASE_URL;
import loader from "../../assets/loader.svg";

const tg = ref({});
const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  statuses: {
    type: Array,
    required: true,
  },
});
const userRoleId = localStorage.getItem("user_role_id");
const statuses = ref([]);
const newComment = ref("");
const comments = ref([]);
const commentMedia = ref("");
const commentMediaPreview = ref("");
const isCommentUpdate = ref(false);
const updatedComment = ref("");
const isLoading = ref(true);

const formatDate = (date: string) => {
  if (date === null || date === undefined || date === "null") {
    return "N/A";
  } else {
    return new Date(date).toLocaleDateString();
  }
};

const formatDateTime = (date: string) => {
  if (date === null || date === undefined || date === "null") {
    return "N/A";
  } else {
    return new Date(date).toLocaleString();
  }
};

const fetchTg = async () => {
  if (!props.id) return;
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(`${baseUrl}/tour-guide/get-by-id/${props.id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    tg.value = response.data.data;
  } catch (error) {
    console.error(error);
  }
};

const updateTgStatus = async (status: string) => {
  const token = localStorage.getItem("token");

  const newStatus = props.statuses.find((s) => s.name === status);
  try {
    const response = await axios.post(
      `${baseUrl}/tour-guide/update`,
      {
        id: props.id,
        status_id: newStatus.id,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (newStatus.name === "approved") {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Tour guide has been approved successfully",
      });
    } else if (newStatus.name === "refused") {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Tour guide has been refused successfully",
      });
    } else if (newStatus.name === "published") {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Tour guide has been published successfully",
      });
    } else if (newStatus.name === "unpublished") {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Tour guide has been unpublished successfully",
      });
    }
    await fetchTg();
  } catch (error) {
    console.error(error);
  }
};

const storeComment = async () => {
  const token = localStorage.getItem("token");
  const type = "tg";
  const blogId = props.id;
  try {
    const formData = new FormData();
    formData.append("content", newComment.value);
    if (commentMedia.value) {
      formData.append("media", commentMedia.value);
    }

    const response = await axios.post(`${baseUrl}/comment/store/${blogId}/${type}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 200) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Comment has been added successfully",
      });
      newComment.value = "";
      commentMedia.value = "";
      commentMediaPreview.value = "";
    }
    await fetchComments();
  } catch (error) {
    console.error(error);
  }
};

const fetchComments = async () => {
  const token = localStorage.getItem("token");
  const type = "tg";
  const blogId = props.id;
  try {
    const response = await axios.get(`${baseUrl}/comment/get/${blogId}/${type}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    comments.value = response.data.data.reverse();
  } catch (error) {
    console.error(error);
  }
};

const handleCommentMediaUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    commentMedia.value = file;

    // Generate a preview URL
    commentMediaPreview.value = URL.createObjectURL(file);
  }
};

const updateComment = async (commentId) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.put(
      `${baseUrl}/comment/update/${commentId}`,
      {
        content: updatedComment.value,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.status === 200) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Comment has been updated successfully",
      });
      updatedComment.value = "";
      isCommentUpdate.value = false;
    }
    await fetchComments();
  } catch (error) {
    console.error(error);
  }
};

const isVideo = (index) => {
  try {
    const files = JSON.parse(tg.value.carousel);
    const file = files[index];
    return typeof file === "string" && file.match(/\.(mp4|webm|ogg)$/i);
  } catch (error) {
    console.error("Invalid JSON in tg.value.carousel:", error);
    return false;
  }
};

watch(
  () => props.id,
  async (newId) => {
    if (newId) {
      await fetchTg();
      await fetchComments();
      isLoading.value = false;
    }
  }
);

onMounted(async () => {
  await fetchTg();
});
</script>

<style>
.content h1 {
  font-size: 2.25rem;
  line-height: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.content h2 {
  font-size: 1.875rem;
  line-height: 2.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.content h3 {
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.content h4 {
  font-size: 1.25rem;
  line-height: 1.75rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.content h5 {
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.content h6 {
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.content p {
  font-size: 1rem;
  margin-bottom: 1rem;
}

.content ul {
  list-style-type: disc;
  margin-left: 1.5rem;
}

.content li {
  /* margin-bottom: 0.5rem; */
  font-size: 1.1rem;
  margin-left: 1.5rem;
}

.content ol {
  list-style-type: decimal;
  margin-left: 1.5rem;
}
</style>
